/*!
 * v-title v1.0.13
 * (c) 2019-2020 yujinpan
 * Released under the MIT License.
 */

import debounce from 'lodash/debounce';
import Tooltip from 'tooltip.js';
import { getStyle } from 'element-ui/lib/utils/dom';

var zIndex = 4000;
var refs = {};
var tooltipId = 0;
function tooltipCreate(el, title, _ref) {
  var effect = _ref.effect,
      placement = _ref.placement,
      className = _ref.className,
      maxWidth = _ref.maxWidth;
  var id = el.dataset.tooltipId = el.dataset.tooltipId || String(++tooltipId);
  var tooltip;

  if (!refs.hasOwnProperty(id)) {
    tooltip = new Tooltip(el, {
      placement: placement,
      template: "\n        <div\n          class=\"v-title tooltip el-tooltip__popper is-".concat(effect, " ").concat(className, "\"\n          role=\"tooltip\"\n          style=\"").concat(maxWidth ? 'max-width:' + maxWidth + 'px' : '', ";transition: opacity 200ms linear;opacity: 0;z-index: ").concat(zIndex, "\">\n          <div class=\"tooltip__arrow popper__arrow\"></div>\n          <div class=\"tooltip__inner\"></div>\n        </div>\n      "),
      title: title,
      trigger: 'manual',
      popperOptions: {
        modifiers: {
          preventOverflow: {
            enabled: false
          },
          hide: {
            enabled: false
          }
        },
        removeOnDestroy: true
      },
      container: document.body
    });
    tooltip.show();
    refs[id] = tooltip;
  } else {
    tooltip = refs[id];
    clearTimeout(tooltip._timeout);
  }

  tooltip.popperInstance && (tooltip.popperInstance.popper.style.opacity = 1);
  return tooltip;
}
function tooltipRemove(el) {
  var delayHide = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 200;
  var id = el.dataset.tooltipId;
  var tooltip = refs[id];

  if (tooltip) {
    if (tooltip._timeout) clearTimeout(tooltip._timeout);
    tooltip.popperInstance && (tooltip.popperInstance.popper.style.opacity = 0);

    var destroy = function destroy() {
      tooltip.dispose();
      delete refs[id];
    };

    if (delayHide > 0) {
      tooltip._timeout = setTimeout(function () {
        destroy();
      }, delayHide);
    } else {
      destroy();
    }
  }
}
function tooltipUpdateTitle(el, title) {
  var id = el.dataset.tooltipId;
  var tooltip = refs[id];

  if (tooltip) {
    tooltip.updateTitleContent(title);
  }
}

function getStyleToInt(elem, attribute) {
  return parseInt(getStyle(elem, attribute), 10) || 0;
}

var tooltipData = {};
var tooltipIdName = 'tooltip-id';
var tooltipId$1 = 0;
var VTitle = {
  inserted: function inserted(el, binding) {
    var id = ++tooltipId$1;
    el.setAttribute(tooltipIdName, id + '');
    var data = tooltipData[id] = {
      target: el,
      tooltip: null,
      id: id,
      activateTooltip: null,
      _deactivateTooltip: null,
      deactivateTooltip: null,
      text: binding.value
    };
    var effect = binding.modifiers.light ? 'light' : 'dark';
    var maxWidth = el.getAttribute('title-max-width');
    var className = el.getAttribute('title-class-name');
    var placement = el.getAttribute('title-placement') || 'top';
    var delayTime = el.getAttribute('title-delay-time') || 200;
    var overflow = binding.modifiers.overflow;
    var multiple = binding.modifiers.multiple;
    var delay = binding.modifiers.delay;

    data._deactivateTooltip = function () {
      var delayHide = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 200;
      var tooltipElem = getPopperElemFromTooltipData(data);

      if (tooltipElem) {
        tooltipElem.removeEventListener('mouseenter', data.deactivateTooltip.cancel, false);
        tooltipElem.removeEventListener('mouseleave', data.deactivateTooltip, false);
      }

      deactivateTooltip(data.target, delayHide);
      data.tooltip = null;
    };

    data.deactivateTooltip = debounce(function () {
      return data._deactivateTooltip();
    }, 250);
    data.activateTooltip = debounce(function () {
      data.tooltip = activateTooltip(data.target, data.text, {
        effect: effect,
        placement: placement,
        overflow: overflow,
        multiple: multiple,
        maxWidth: maxWidth,
        className: className
      });
      var tooltipElem = getPopperElemFromTooltipData(data);

      if (tooltipElem) {
        tooltipElem.addEventListener('mouseenter', data.deactivateTooltip.cancel, false);
        tooltipElem.addEventListener('mouseleave', data.deactivateTooltip, false);
      }
    }, delay ? delayTime : 0);
    el.addEventListener('mouseenter', data.activateTooltip, false);
    el.addEventListener('mouseenter', data.deactivateTooltip.cancel, false);
    el.addEventListener('mouseleave', data.deactivateTooltip, false);
    el.addEventListener('mouseleave', data.activateTooltip.cancel, false);
    el.addEventListener('click', data._deactivateTooltip, false);
  },
  componentUpdated: function componentUpdated(el, binding) {
    var data = getTooltipData(el);

    if (data) {
      data.text = binding.value;
      tooltipUpdateTitle(el, binding.value);
    }
  },
  unbind: function unbind(el) {
    var data = getTooltipData(el);

    if (data) {
      el.removeEventListener('mouseenter', data.activateTooltip, false);
      el.removeEventListener('mouseenter', data.deactivateTooltip.cancel, false);
      el.removeEventListener('mouseleave', data.deactivateTooltip, false);
      el.removeEventListener('mouseleave', data.activateTooltip.cancel, false);
      el.removeEventListener('click', data._deactivateTooltip, false);

      data._deactivateTooltip(0);

      removeTooltipData(el);
    }
  }
};

function activateTooltip(el, title, _ref) {
  var effect = _ref.effect,
      placement = _ref.placement,
      overflow = _ref.overflow,
      multiple = _ref.multiple,
      maxWidth = _ref.maxWidth,
      className = _ref.className;

  if (!overflow || overflow && checkOverflow(el, multiple)) {
    return tooltipCreate(el, title, {
      effect: effect,
      placement: placement,
      maxWidth: maxWidth,
      className: className
    });
  }
}

function deactivateTooltip(el, delayHide) {
  tooltipRemove(el, delayHide);
}

function checkOverflow(elem) {
  var multiple = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var elemRect = elem.getBoundingClientRect();
  var range = document.createRange();
  range.setStart(elem, 0);
  range.setEnd(elem, elem.childNodes.length);
  var rangeRect = range.getBoundingClientRect();

  if (multiple) {
    var height = rangeRect.height;
    var padding = getStyleToInt(elem, 'paddingTop') + getStyleToInt(elem, 'paddingBottom');
    var border = getStyleToInt(elem, 'borderTopWidth') + getStyleToInt(elem, 'borderBottomWidth');
    return height + padding + border > elemRect.height;
  } else {
    var width = rangeRect.width;

    var _padding = getStyleToInt(elem, 'paddingLeft') + getStyleToInt(elem, 'paddingRight');

    var _border = getStyleToInt(elem, 'borderLeftWidth') + getStyleToInt(elem, 'borderRightWidth');

    return width + _padding + _border > elemRect.width;
  }
}

function getTooltipData(el) {
  var id = el.getAttribute(tooltipIdName);

  if (tooltipData.hasOwnProperty(id)) {
    return tooltipData[id];
  } else {
    return null;
  }
}

function removeTooltipData(el) {
  var id = el.getAttribute(tooltipIdName);

  if (tooltipData.hasOwnProperty(id)) {
    delete tooltipData[id];
  }
}

function getPopperElemFromTooltipData(data) {
  return data.tooltip && data.tooltip.popperInstance && data.tooltip.popperInstance.popper;
}

VTitle.install = function (Vue) {
  Vue.directive('title', VTitle);
};

export default VTitle;
export { tooltipCreate, tooltipRemove, tooltipUpdateTitle };
//# sourceMappingURL=index.js.map
